import { useState, useEffect, useCallback, useRef } from 'react';
import { assessmentProgressApi } from '../services/api';

interface InternalAssessmentProgress {
  swot: number;
  industrySuccessFactors: number;
  overall: number;
}

const emptyProgress: InternalAssessmentProgress = {
  swot: 0,
  industrySuccessFactors: 0,
  overall: 0,
};

export function useInternalAssessmentProgress(companyId: string) {
  const [progress, setProgress] = useState<InternalAssessmentProgress>(emptyProgress);
  const [isLoading, setIsLoading] = useState(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const requestVersion = useRef(0);

  const refreshProgress = useCallback(async () => {
    if (!companyId) return;
    const version = ++requestVersion.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = await assessmentProgressApi.getInternalProgress(companyId);
      if (version !== requestVersion.current) return;
      if (response.success && response.data) {
        setProgress({
          swot: response.data.sections.swot,
          industrySuccessFactors: response.data.sections.industrySuccessFactors,
          overall: response.data.overall,
        });
      } else {
        setError(response.error || 'Internal assessment progress is unavailable.');
      }
    } catch {
      if (version === requestVersion.current) {
        setError('Internal assessment progress is unavailable.');
      }
    } finally {
      if (version === requestVersion.current) setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    setProgress(emptyProgress);
    setError(null);
    setIsLoading(Boolean(companyId));
    void refreshProgress();
    return () => {
      requestVersion.current += 1;
    };
  }, [companyId, refreshProgress]);

  return { progress, isLoading, error, refreshProgress };
}
